import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtA = (v) => (v == null ? '—' : `A$${Math.round(v).toLocaleString()}`);
const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

/** Preview + one-click push of a role's recommended sell rate into the global rate card. */
export function ApplyTargetRateDialog({ role, rate, label, open, onClose, canApply, onApplied, initialRegion = '', revertToGlobal = false, title }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);
  const [region, setRegion] = useState('');
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    if (!open) return;
    setRegion(initialRegion || '');
    axios.get(`${API_URL}/api/insights/margin/role-pricing/regions`, { withCredentials: true })
      .then((r) => setRegions(r.data.regions || [])).catch(() => setRegions([]));
  }, [open, initialRegion]);

  useEffect(() => {
    if (!open || !role) return;
    setPreview(null); setError(null);
    axios.get(`${API_URL}/api/insights/margin/role-pricing/apply-target/preview`, { params: { role, rate, ...(region ? { region } : {}) }, withCredentials: true })
      .then((r) => setPreview(r.data)).catch((e) => setError(e?.response?.data?.detail || 'Could not build preview'));
  }, [open, role, rate, region]);

  const scopeLabel = region || 'Global';
  const apply = async () => {
    setApplying(true);
    try {
      const r = await axios.post(`${API_URL}/api/insights/margin/role-pricing/apply-target`, { role, rate: revertToGlobal && region ? null : rate, region: region || null }, { withCredentials: true });
      toast.success(r.data.message);
      onApplied?.(r.data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not apply rate'); } finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto" data-testid="apply-target-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2040]">{title || 'Apply target rate'} — {role} {label ? `· ${label}` : ''}</DialogTitle>
          <DialogDescription>
            Sets the <b>{scopeLabel}</b> rate card {role} sell rate to <b>A${Number(rate).toFixed(0)}/h</b>{preview ? ` (currently A$${Number(preview.current_rate).toFixed(0)}/h${region && preview.current_regional_rate == null ? ', inherited from global' : ''})` : ''}.
            {region ? ` Only ${region} deals move; other regions and the global card are untouched.` : ' Estimates with a per-estimate override or a regional rate card for this role are not affected.'} Affected reps get an in-app "rate change" notification asking them to reprice.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 text-xs" data-testid="apply-target-scope">
          <span className="text-slate-500">Scope</span>
          <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="apply-target-region-select"
            className="h-7 text-[11px] rounded-md border border-slate-200 bg-white px-2 text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]">
            <option value="">Global rate card</option>
            {regions.map((r) => <option key={r} value={r}>{r} regional rate card</option>)}
          </select>
        </div>
        {error && <p className="text-xs text-rose-600" data-testid="apply-target-error">{error}</p>}
        {!preview && !error && <div className="flex items-center gap-2 text-xs text-slate-400 py-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Building preview…</div>}
        {preview && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-xs" data-testid="apply-target-summary">
              <span className="rounded-md border border-slate-200 px-2 py-1"><b>{preview.affected}</b> open deal(s) affected</span>
              <span className={`rounded-md border border-slate-200 px-2 py-1 ${preview.total_delta_sell_aud >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{preview.total_delta_sell_aud >= 0 ? '+' : '−'}{fmtA(Math.abs(preview.total_delta_sell_aud))} sell on reprice</span>
              {preview.skipped.length > 0 && <span className="rounded-md border border-slate-200 px-2 py-1 text-slate-500">{preview.skipped.length} skipped (override / regional)</span>}
            </div>
            {preview.deals.length > 0 && (
              <div className="rounded-lg border border-slate-200 overflow-x-auto">
                <table className="w-full text-xs" data-testid="apply-target-table">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Estimate</th>
                      <th className="text-left px-2 py-2 font-semibold">Rep</th>
                      <th className="text-right px-2 py-2 font-semibold">{role} hours</th>
                      <th className="text-right px-2 py-2 font-semibold">Rate now → new</th>
                      <th className="text-right px-2 py-2 font-semibold">Δ sell</th>
                      <th className="text-right px-3 py-2 font-semibold">Margin now → after</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.deals.map((d) => (
                      <tr key={d.id} className="border-t border-slate-100" data-testid={`apply-target-deal-${d.id}`}>
                        <td className="px-3 py-1.5 font-medium text-[#0F2040] max-w-[240px] truncate">{d.name}<span className="text-slate-400 font-normal"> · {d.organization_name || '—'}</span></td>
                        <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{d.rep_name || 'Unassigned'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{d.hours.toFixed(1)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">A${d.current_rate_aud.toFixed(0)} → A${d.new_rate_aud.toFixed(0)}{d.blanket_discount_pct ? <span className="text-slate-400"> ({d.blanket_discount_pct}% blanket)</span> : null}</td>
                        <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${d.delta_sell_aud >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{d.delta_sell_aud >= 0 ? '+' : '−'}{fmtA(Math.abs(d.delta_sell_aud))}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{pct(d.margin_pct_now)} → <b>{pct(d.margin_pct_after)}</b></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {preview.skipped.length > 0 && (
              <p className="text-[10px] text-slate-500" data-testid="apply-target-skipped">Not affected: {preview.skipped.map((s) => `${s.name} (${s.reason})`).join(' · ')}</p>
            )}
            <p className="text-[10px] text-amber-700" data-testid="apply-target-note">{preview.note}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose} data-testid="apply-target-cancel">Cancel</Button>
              {canApply ? (
                <Button size="sm" className="bg-[#0F2040] hover:bg-[#1a2f57] text-white" onClick={apply} disabled={applying} data-testid="apply-target-confirm">{applying ? 'Applying…' : revertToGlobal && region ? `Remove ${region} ${role} override (inherit A$${Number(rate).toFixed(0)}/h)` : `Set ${scopeLabel} ${role} rate to A$${Number(rate).toFixed(0)}/h`}</Button>
              ) : <span className="text-[11px] text-slate-500 self-center" data-testid="apply-target-no-permission">CRO, CCO or Super Admin can apply this.</span>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
